import React, { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { AdHeroSlide, useAdCampaigns } from "@/components/AdPlacement";
import {
  Sparkles, ArrowRight, ChevronRight, ChevronLeft, GraduationCap,
  UploadCloud, Target, Users, ListChecks, Handshake, CheckCircle2,
  Flame, PenLine, ArrowLeftRight, Home, UserCircle2, Pause, Play,
} from "lucide-react";

const NAVY = "#0F2C59";
const GOLD = "#D4A017";

/* WAVE 2 — EDUCATION MODE (carousel).
   Authentic PropertyConnect product-education & feature-discovery surface. NOT
   advertising: no campaigns, sponsors, payments or advertiser creatives, and
   fully independent of the Match Engine, Opportunity Engine, Sales Coach ranking
   and organic Marketplace ranking. Every CTA routes to a real in-app workflow.

   The carousel (SpotlightCarousel) is a REUSABLE presentation surface: future
   Featured Property / Project / Professional / Events / Recruitment / Sponsored
   placements can be added as additional slide objects without another redesign. */

const WORKFLOW = [
  { k: "POST", icon: UploadCloud, sub: "Properties & Buyer Needs" },
  { k: "MATCH", icon: Target, sub: "Find relevant opportunities" },
  { k: "CONNECT", icon: Users, sub: "Engage with professionals" },
  { k: "PRIORITIZE", icon: ListChecks, sub: "AI Sales Coach guides next actions" },
  { k: "COLLABORATE", icon: Handshake, sub: "Move opportunities forward" },
  { k: "CLOSE", icon: CheckCircle2, sub: "Close the deal" },
];

function scrollToSalesCoach() {
  const el = document.querySelector('[data-testid="sales-coach"]');
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const heading = el.querySelector('[data-testid="sales-coach-headline"]') || el;
    if (heading && heading.setAttribute) {
      heading.setAttribute("tabindex", "-1");
      try { heading.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
  }
}

function FeatureBadge({ icon: Icon, children, tone, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] leading-tight"
      style={{ backgroundColor: tone.chipBg, color: tone.chipText, border: `1px solid ${tone.chipBorder}` }}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" /> <span>{children}</span>
    </span>
  );
}

/* ---- Stylized mini-UI illustrations (no stock photos) ---- */
function CoachViz() {
  const rows = [
    { dot: "#DC2626", label: "High Priority", chip: "Respond Today" },
    { dot: "#0284C7", label: "New Matches", chip: null },
    { dot: "#D4A017", label: "Follow Up", chip: null },
    { dot: "#059669", label: "Growth / Visibility", chip: null },
  ];
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-2 rounded-lg bg-white/80 border border-slate-100 px-2.5 py-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: r.dot }} />
          <span className="text-[11.5px] font-semibold text-slate-700 truncate">{r.label}</span>
          {r.chip && <span className="ml-auto text-[9.5px] font-bold uppercase tracking-wide text-red-600 bg-red-50 rounded px-1.5 py-0.5 shrink-0">{r.chip}</span>}
        </div>
      ))}
    </div>
  );
}

function ListingViz() {
  return (
    <div className="rounded-lg bg-white/80 border border-slate-100 p-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="h-2.5 flex-1 rounded-full bg-slate-200" />
        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5"><Sparkles className="h-2.5 w-2.5" /> AI</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100" />
      <div className="h-1.5 w-4/5 rounded-full bg-slate-100" />
      <div className="flex gap-1.5 pt-0.5">
        <span className="text-[9px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Selling points</span>
        <span className="text-[9px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Description</span>
      </div>
    </div>
  );
}

function MatchViz() {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 rounded-lg bg-white/80 border border-slate-100 px-2 py-2 text-center">
        <Home className="h-4 w-4 mx-auto text-slate-500" />
        <div className="text-[10px] font-semibold text-slate-600 mt-1 leading-tight">Property</div>
      </div>
      <div className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(5,150,105,0.12)", color: "#059669" }}>
        <ArrowLeftRight className="h-4 w-4" />
      </div>
      <div className="flex-1 rounded-lg bg-white/80 border border-slate-100 px-2 py-2 text-center">
        <UserCircle2 className="h-4 w-4 mx-auto text-slate-500" />
        <div className="text-[10px] font-semibold text-slate-600 mt-1 leading-tight">Buyer Need</div>
      </div>
    </div>
  );
}

function CollabViz() {
  return (
    <div className="flex items-center justify-center gap-2 py-1">
      <div className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
        <UserCircle2 className="h-5 w-5 text-slate-400" />
      </div>
      <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(124,58,237,0.12)", color: "#7C3AED" }}>
        <Handshake className="h-4 w-4" />
      </div>
      <div className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
        <UserCircle2 className="h-5 w-5 text-slate-400" />
      </div>
      <span className="ml-1 text-[10px] font-semibold text-violet-700 bg-violet-50 rounded-full px-2 py-1 whitespace-nowrap">Buyer Need</span>
    </div>
  );
}

const TONES = {
  coach: { chipBg: "rgba(212,160,23,0.12)", chipText: "#92600B", chipBorder: "rgba(212,160,23,0.4)", panel: "rgba(212,160,23,0.07)" },
  listing: { chipBg: "rgba(37,99,235,0.10)", chipText: "#1D4ED8", chipBorder: "rgba(37,99,235,0.28)", panel: "rgba(37,99,235,0.05)" },
  matching: { chipBg: "rgba(5,150,105,0.10)", chipText: "#047857", chipBorder: "rgba(5,150,105,0.28)", panel: "rgba(5,150,105,0.05)" },
  buyer: { chipBg: "rgba(124,58,237,0.10)", chipText: "#6D28D9", chipBorder: "rgba(124,58,237,0.28)", panel: "rgba(124,58,237,0.05)" },
};

/* ---- Slide 1: opportunity-ecosystem hero (navy) ---- */
function HeroSlide() {
  return (
    <div
      data-testid="spotlight-hero"
      className="relative h-full overflow-hidden rounded-2xl border"
      style={{ background: "linear-gradient(160deg, #143163 0%, #0F2C59 55%, #0C2247 100%)", borderColor: "rgba(212,160,23,0.25)" }}
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: "radial-gradient(120% 120% at 100% 0%, rgba(212,160,23,0.18) 0%, rgba(212,160,23,0) 45%)" }} />
      <div className="relative z-10 p-5 sm:p-6 text-white">
        <FeatureBadge icon={Sparkles} tone={{ chipBg: "rgba(212,160,23,0.16)", chipText: GOLD, chipBorder: "rgba(212,160,23,0.4)" }} testid="spotlight-hero-badge">
          PropertyConnect Feature Spotlight
        </FeatureBadge>
        <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight tracking-tight mt-3">
          Turn Properties &amp; Buyer Needs Into Opportunities
        </h3>
        <div className="h-1 w-14 rounded-full mt-2.5" style={{ backgroundColor: GOLD }} aria-hidden="true" />
        <p className="text-[13.5px] leading-relaxed text-slate-200 mt-3">
          Post the properties you represent and the requirements of your buyers. PropertyConnect connects supply and demand so you can discover relevant matches, inquiries and collaboration opportunities.
        </p>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="spotlight-workflow">
          {WORKFLOW.map((s) => (
            <div key={s.k} className="rounded-xl px-2.5 py-2.5 text-center" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <div className="mx-auto h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "rgba(212,160,23,0.15)", color: GOLD }}>
                <s.icon className="h-4 w-4" />
              </div>
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] mt-1.5">{s.k}</div>
              <div className="text-[9.5px] text-slate-300/90 leading-snug mt-0.5">{s.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <Link
            to="/opportunities"
            data-testid="spotlight-hero-cta"
            className="inline-flex items-center justify-center gap-2 font-semibold text-[13.5px] px-4 py-2.5 rounded-xl active:scale-[0.98] transition-transform"
            style={{ backgroundColor: GOLD, color: NAVY }}
          >
            Explore My Opportunities <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const CARDS = [
  { slug: "feature-coach", tone: TONES.coach, badgeIcon: Flame, badge: "AI Sales Coach",
    title: "Know What to Do Next",
    copy: "PropertyConnect Sales Coach turns your real opportunities into prioritized actions—helping you focus on inquiries, collaborations, matches and growth activities that deserve your attention today.",
    cta: "View My Sales Coach", viz: CoachViz, action: "scroll" },
  { slug: "feature-listing", tone: TONES.listing, badgeIcon: PenLine, badge: "AI Listing Assistant",
    title: "Create Better Property Listings",
    copy: "Use PropertyConnect to improve listing titles, draft descriptions and highlight selling points while keeping you in control of the final content.",
    cta: "Add a Property", viz: ListingViz, to: "/have-property" },
  { slug: "feature-matching", tone: TONES.matching, badgeIcon: Target, badge: "Smart Matching",
    title: "Let Opportunities Find Each Other",
    copy: "PropertyConnect compares properties with Buyer Needs to surface relevant opportunities based on actual requirements.",
    cta: "Explore Opportunities", viz: MatchViz, to: "/opportunities" },
  { slug: "feature-buyer", tone: TONES.buyer, badgeIcon: Handshake, badge: "Buyer Needs & Collaboration",
    title: "Turn Your Network Into Opportunity",
    copy: "Post what your buyer is looking for so PropertyConnect can surface relevant inventory and help connect you with professionals who may have matching properties.",
    cta: "Post Buyer Need", viz: CollabViz, to: "/have-buyer" },
];

function FeatureSlide({ card }) {
  const Viz = card.viz;
  const cls = "mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold self-start rounded-lg px-3.5 py-2 transition-colors";
  const style = { color: card.tone.chipText, backgroundColor: card.tone.panel, border: `1px solid ${card.tone.chipBorder}` };
  const label = <>{card.cta} <ArrowRight className="h-3.5 w-3.5" /></>;
  const cta = card.action === "scroll"
    ? <button type="button" data-testid={`spotlight-${card.slug}-cta`} onClick={scrollToSalesCoach} className={cls} style={style}>{label}</button>
    : <Link to={card.to} data-testid={`spotlight-${card.slug}-cta`} className={cls} style={style}>{label}</Link>;
  return (
    <div data-testid={`spotlight-${card.slug}`} className="h-full bg-card border border-border rounded-2xl flex flex-col p-5 sm:p-6">
      <div className="rounded-xl p-4 flex items-center" style={{ backgroundColor: card.tone.panel }}>
        <div className="w-full max-w-sm mx-auto"><Viz /></div>
      </div>
      <div className="flex flex-1 flex-col pt-4">
        <FeatureBadge icon={card.badgeIcon} tone={card.tone} testid={`spotlight-${card.slug}-badge`}>{card.badge}</FeatureBadge>
        <h4 className="font-display text-lg font-semibold text-slate-900 leading-snug mt-2.5">{card.title}</h4>
        <p className="text-[13px] text-slate-500 leading-relaxed mt-1.5">{card.copy}</p>
        <div className="mt-auto">{cta}</div>
      </div>
    </div>
  );
}

/* ---- Reusable carousel surface (one slide at a time; manual nav + dots) ---- */
const AUTO_MS = 5000;
const SLIDE_MS = 400;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  return reduced;
}

function usePageVisible() {
  const [visible, setVisible] = useState(!document.hidden);
  useEffect(() => {
    const on = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

export function SpotlightCarousel({ slides, ariaLabel = "PropertyConnect Spotlight" }) {
  const count = slides.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);   // explicit user pause/resume
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduced = usePrefersReducedMotion();
  const pageVisible = usePageVisible();
  const touchX = useRef(null);
  // Mobile (<640px): the carousel viewport hugs the ACTIVE slide's height so a compact
  // advertising creative is not stretched into the tall editorial-slide navy stage.
  // Desktop is unchanged (equal-height stretch via sm:min-h on each slide).
  const slideRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(false);
  const [vh, setVh] = useState(undefined);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  useEffect(() => {
    if (!isMobile) { setVh(undefined); return undefined; }
    const measure = () => { const el = slideRefs.current[idx]; if (el) setVh(el.offsetHeight); };
    measure();
    const el = slideRefs.current[idx];
    let ro;
    if (el && window.ResizeObserver) { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 350);
    return () => { if (ro) ro.disconnect(); window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [idx, isMobile, count]);

  const go = useCallback((n) => setIdx(((n % count) + count) % count), [count]);
  const next = useCallback(() => go(idx + 1), [go, idx]);
  const prev = useCallback(() => go(idx - 1), [go, idx]);

  // Auto-advance is active only when nothing should hold it. Because the effect
  // depends on `idx`, any manual navigation (which changes idx) naturally resets the timer.
  const autoActive = !paused && !hovering && !focused && pageVisible && !reduced;
  useEffect(() => {
    if (!autoActive) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % count), AUTO_MS);
    return () => clearTimeout(t);
  }, [idx, autoActive, count]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
    touchX.current = null;
  };

  return (
    <div
      data-testid="spotlight-carousel"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
      onKeyDown={onKeyDown}
    >
      <div className="relative">
        {/* Viewport + horizontal slide track */}
        <div className="overflow-hidden rounded-2xl transition-[height] duration-300 ease-out" style={isMobile && vh ? { height: vh } : undefined} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div
            data-testid="spotlight-track"
            className="flex items-start sm:items-stretch"
            style={{
              transform: `translateX(-${idx * 100}%)`,
              transition: reduced ? "none" : `transform ${SLIDE_MS}ms cubic-bezier(0.4,0,0.2,1)`,
            }}
          >
            {slides.map((s, i) => (
              <div
                key={i}
                ref={(el) => (slideRefs.current[i] = el)}
                data-testid={`spotlight-slide-${i}`}
                className="w-full shrink-0 sm:min-h-[400px]"
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${i + 1} of ${count}`}
                aria-hidden={i !== idx}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls row: prev / pagination / next / pause — all OUTSIDE the media */}
      <div className="mt-3 flex items-center justify-center gap-2.5">
        <button type="button" data-testid="spotlight-prev" onClick={prev} aria-label="Previous slide"
          className="h-8 w-8 rounded-full bg-white border border-border shadow-sm flex items-center justify-center text-navy hover:bg-slate-50 transition-colors shrink-0">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2" role="tablist" aria-label="Spotlight slides">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              data-testid={`spotlight-dot-${i}`}
              onClick={() => go(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to slide ${i + 1}`}
              className={`relative h-2 rounded-full overflow-hidden transition-all ${i === idx ? "w-6 bg-slate-300" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
            >
              {i === idx && (
                <span
                  key={`${idx}-${autoActive}`}
                  className="absolute inset-y-0 left-0 bg-gold-500"
                  style={autoActive
                    ? { animation: `pc-spot-progress ${AUTO_MS}ms linear forwards` }
                    : { width: "100%" }}
                />
              )}
            </button>
          ))}
        </div>
        <button type="button" data-testid="spotlight-next" onClick={next} aria-label="Next slide"
          className="h-8 w-8 rounded-full bg-white border border-border shadow-sm flex items-center justify-center text-navy hover:bg-slate-50 transition-colors shrink-0">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-testid="spotlight-toggle"
          onClick={() => setPaused((p) => !p)}
          aria-label={paused ? "Resume automatic slideshow" : "Pause automatic slideshow"}
          aria-pressed={paused}
          className="h-7 w-7 rounded-full text-slate-400 hover:text-navy hover:bg-slate-100 flex items-center justify-center transition-colors"
        >
          {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
        </button>
      </div>

      <style>{`@keyframes pc-spot-progress{from{width:0%}to{width:100%}}`}</style>
    </div>
  );
}

export function PropConnectSpotlight({ embedded = false }) {
  const premium = useAdCampaigns("DASHBOARD_SPOTLIGHT");
  const MAX_SLOTS = 5;
  // Eligible campaigns occupy positions first (backend already returns the top 5 by the
  // deterministic priority DESC, created_at ASC rule). Editorial slides fill only remaining slots.
  const premiumSlides = (premium || []).slice(0, MAX_SLOTS).map((c) => (
    <AdHeroSlide key={`ad-${c.id}`} campaign={c} placement="DASHBOARD_SPOTLIGHT" />
  ));
  const editorialAll = [<HeroSlide key="hero" />, ...CARDS.map((c) => <FeatureSlide key={c.slug} card={c} />)];
  const editorial = editorialAll.slice(0, Math.max(0, MAX_SLOTS - premiumSlides.length));
  const slides = [...premiumSlides, ...editorial];
  const hasPaid = premiumSlides.length > 0;
  return (
    <section className={embedded ? "" : "mt-10"} data-testid="dashboard-spotlight" aria-label="PropertyConnect Spotlight">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: GOLD }}>
          <Sparkles className="h-3.5 w-3.5" /> PropertyConnect Spotlight
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 mt-1.5">Discover Opportunities with PropertyConnect</h2>
        <p className="text-sm text-slate-500 mt-0.5">Featured properties, projects, professionals, tools and opportunities across the network.</p>
      </div>

      <SpotlightCarousel slides={slides} />

      <p data-testid="spotlight-disclosure" className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500 mt-4">
        <GraduationCap className="h-3.5 w-3.5" /> {hasPaid
          ? "PropertyConnect Spotlight · Includes sponsored campaigns, clearly labelled, plus product education."
          : "PropertyConnect Spotlight · Product education and feature discovery."}
      </p>
    </section>
  );
}
